<template>
  <div class="scrollable-container mr-4">
    <div>
      <v-dialog v-model="isLoading" hide-overlay persistent width="300">
        <v-card color="primary" dark>
          <v-card-text>
            {{ loadingStatus }}
            <v-progress-linear indeterminate color="white" class="mb-0" />
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Erreur IRIS -->
      <v-alert v-if="irisError" type="error" class="ma-2">{{ irisError }}</v-alert>

      <v-card class="ma-3 -mr-2!">
        <v-card-title class="subheading pb-1">
          {{ $t('Pilot.QuickControl.Actions.Title') }}
          <!-- État glider -->
          <QuickControlStateBar :mission="mission" />
        </v-card-title>
        <v-card-text>
          <v-layout row wrap>
            <v-btn small class="mr-2 mb-2" color="primary" @click="sendCommand('dive')">
              <v-icon left small>mdi-arrow-down-circle</v-icon>
              {{ $t('Pilot.QuickControl.Actions.Dive') }}
            </v-btn>
            <v-btn small class="mr-2 mb-2" :input-value="surfaceActive" @click="sendCommand('surface')">
              <v-icon left small>mdi-waves</v-icon>
              {{ $t('Pilot.QuickControl.Actions.Surface') }}
              <v-icon right small color="green" v-if="surfaceActive">mdi-check-circle</v-icon>
            </v-btn>
            <v-btn small class="mr-2 mb-2" color="warning" @click="sendCommand('ack_alarm')" :disabled="!activeAlarm">
              <v-icon left small>mdi-bell-off</v-icon>
              {{ $t('Pilot.QuickControl.Actions.AckAlarm') }}
            </v-btn>
            <v-btn small class="mr-2 mb-2" @click="sendCommand('download')">
              <v-icon left small>mdi-download</v-icon>
              {{ $t('Pilot.QuickControl.Actions.Download') }}
            </v-btn>
            <v-btn
              small
              class="mr-2 mb-2"
              :input-value="flasherEnabled"
              :disabled="flasherPending"
              @click="sendCommand('flasher')"
            >
              <v-icon left small>mdi-flash</v-icon>
              {{ $t('Pilot.QuickControl.Actions.Flasher') }}
              <v-icon right small color="green" v-if="flasherEnabled">mdi-check-circle</v-icon>
            </v-btn>
            <v-btn
              small
              class="mr-2 mb-2"
              :input-value="radioEnabled"
              :disabled="radioPending"
              @click="sendCommand('radio')"
            >
              <v-icon left small>mdi-radio-tower</v-icon>
              {{ $t('Pilot.QuickControl.Actions.Radio') }}
              <v-icon right small color="green" v-if="radioEnabled">mdi-check-circle</v-icon>
            </v-btn>
            <v-btn
              small
              class="mr-2 mb-2"
              :input-value="payloadEnabled"
              :disabled="payloadPending"
              @click="sendCommand('payload')"
            >
              <v-icon left small>mdi-package-variant</v-icon>
              {{ $t('Pilot.QuickControl.Actions.Payload') }}
              <v-icon right small color="green" v-if="payloadEnabled">mdi-check-circle</v-icon>
            </v-btn>
            <v-btn
              small
              class="mr-2 mb-2"
              :input-value="missionPlanActive"
              :disabled="missionPlanActive"
              @click="activateMissionPlan"
              :loading="missionPlanLoading"
            >
              <v-icon left small>mdi-map-marker-path</v-icon>
              {{ $t('Pilot.QuickControl.Actions.MissionPlan') }}
              <v-icon right small color="green" v-if="missionPlanActive">mdi-check-circle</v-icon>
            </v-btn>
          </v-layout>
        </v-card-text>
      </v-card>

      <!-- Tables de commandes -->
      <v-card class="ma-3 -mr-2!">
        <v-card-title class="subheading pb-1">{{ $t('Pilot.QuickControl.Tables.Pending') }}</v-card-title>
        <div class="px-2 pb-2">
          <span v-if="!allPendingCommands.length" class="grey--text caption">—</span>
          <v-tooltip v-for="(row, i) in allPendingCommands" :key="row.key" bottom :disabled="!row.tooltip">
            <v-chip
              slot="activator"
              small
              :close="row.removable && !row.sending"
              class="mr-1 mb-1"
              :color="row.color"
              @input="removePendingRow(row)"
            >
              <v-avatar color="#ccc" class="darken-1">{{ i + 1 }}</v-avatar>
              {{ row.command }}
              <v-progress-circular v-if="row.sending" indeterminate size="14" width="2" class="ml-1" />
            </v-chip>
            <span>{{ row.tooltip }}</span>
          </v-tooltip>
        </div>
        <v-divider />
        <div class="cmd-side-by-side">
          <div class="cmd-side">
            <div class="subheading cmd-side-title">{{ $t('Pilot.QuickControl.Tables.Accepted') }}</div>
            <div ref="acceptedScroll" class="cmd-side-scroll">
              <table class="cmd-table">
                <thead>
                  <tr>
                    <th>{{ $t('Pilot.QuickControl.Tables.ColCommand') }}</th>
                    <th>{{ $t('Pilot.QuickControl.Tables.ColDate') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!acceptedCommands.length"><td colspan="2" class="grey--text pl-2">—</td></tr>
                  <tr v-for="(row, i) in acceptedCommands" :key="i">
                    <td>{{ row.command }}</td>
                    <td>{{ row.date }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <!-- Refused commands table masqué temporairement — variables et logique conservées
          <div class="cmd-side">
            <div class="subheading cmd-side-title">{{ $t('Pilot.QuickControl.Tables.Refused') }}</div>
            <div ref="refusedScroll" class="cmd-side-scroll">
              <table class="cmd-table">
                <thead>
                  <tr>
                    <th>{{ $t('Pilot.QuickControl.Tables.ColCommand') }}</th>
                    <th>{{ $t('Pilot.QuickControl.Tables.ColDate') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!refusedCommands.length"><td colspan="2" class="grey--text pl-2">—</td></tr>
                  <tr v-for="(row, i) in refusedCommands" :key="i">
                    <td>{{ row.command }}</td>
                    <td>{{ row.date }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          -->
        </div>
      </v-card>

      <!-- Easy Waypoint -->
      <QuickControlEasyWaypoint
        class="ma-3 -mr-2!"
        v-if="!isLoading && quickcontrolConfig"
        :misId="misId"
        :quickcontrolConfig="quickcontrolConfig"
        :gliderRuntime="mission.gliderRuntime"
        @saved="onConfigSaved"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import QuickControlStateBar from './QuickControlStateBar.vue'
import QuickControlEasyWaypoint from './QuickControlEasyWaypoint.vue'
import { GLIDER_MODE } from '@/models/alo/AloMission'
import { buildMissionUIStateId } from '@/models/ui/MissionUiState'
import { WobGbdType } from '@/enum/WobGbdType'

const ACCEPTED_CMD_PREFIX = '>cmd: '

export default {
  name: 'MissionDetailQuickControl',
  components: { QuickControlStateBar, QuickControlEasyWaypoint },
  data() {
    return {
      irisError: null,
      missionPlanLoading: false,
      refusedCommands: [],
      commandBuffer: [],
      bufferProcessing: false,
    }
  },
  computed: {
    ...mapGetters([
      'currentDetailMission',
      'originalQuickControlConfigs',
      'modifiedQuickControlConfigs',
      'quickcontrolConfigLoading',
      'originalMissionPlanConfigs',
      'originalNsdConfigs',
      'missionUiStateByUiid',
      'missionStatus',
    ]),
    mission() {
      return this.currentDetailMission.mission
    },
    misId() {
      return this.mission.misId
    },
    gliId() {
      return this.mission.gliId
    },
    isLoading() {
      return this.quickcontrolConfigLoading
    },
    loadingStatus() {
      return this.missionStatus
    },
    quickcontrolConfig() {
      return this.modifiedQuickControlConfigs[this.misId] ?? null
    },
    isQuickControlEnabled() {
      return this.quickcontrolConfig?.mod_settings?.EnabledMOD?.['module-QuickControl'] === true
    },
    activeAlarm() {
      const alr = this.mission.gliderRuntime?.alarmCode
      return alr != null && alr !== 0 ? alr : null
    },
    // États glider runtime — computeds purs sur mission.gliderRuntime (peuplé
    // initialement via MISSION_IRIS_GLIDER_STATE_REQUEST puis maintenu en temps
    // réel par WobGliderNmea libelle=seadev). Plus de toggle local après ACK :
    // l'UI suit la réalité physique (NMEA SEADEV émise au prochain surfacing).
    surfaceActive() {
      return this.mission.gliderWaitingMode === GLIDER_MODE.GLIDER_MODE_WAITING_FOR_RESUME
    },
    flasherEnabled() {
      return !!this.mission.gliderRuntime?.devices?.flasher
    },
    radioEnabled() {
      return !!this.mission.gliderRuntime?.devices?.radio
    },
    payloadEnabled() {
      return !!this.mission.gliderRuntime?.devices?.payload
    },
    missionUiState() {
      return this.missionUiStateByUiid(buildMissionUIStateId(this.misId, 'detail'))
    },
    pendingCommands() {
      const actions = this.originalNsdConfigs[this.gliId]?.mod_settings?.NextSurfacingDo_next?.listAction ?? []
      return actions.map(cmd => ({
        command: cmd,
        date: '—',
        status: this.$t('Pilot.QuickControl.Tables.StatusPending'),
      }))
    },
    allPendingCommands() {
      const bufferItems = this.commandBuffer.map((item, i) => ({
        key: `buf-${i}`,
        command: item.command,
        source: 'buffer',
        bufferIndex: i,
        sending: i === 0 && this.bufferProcessing,
        removable: true,
        color: undefined,
        tooltip: '',
      }))
      const nextItems = this.nsdNextList.map((cmd, i) => ({
        key: `nsd-next-${i}`,
        command: cmd,
        source: 'nsd-next',
        sending: false,
        removable: false,
        color: '#e0e0e0',
        tooltip: this.$t('Pilot.QuickControl.Tables.NextSurfacingTooltip'),
      }))
      const allItems = this.nsdAllList.map((cmd, i) => ({
        key: `nsd-all-${i}`,
        command: cmd,
        source: 'nsd-all',
        sending: false,
        removable: false,
        color: '#cfe8ff',
        tooltip: this.$t('Pilot.QuickControl.Tables.AllSurfacingTooltip'),
      }))
      const ncdItems = this.ncdList.map((cmd, i) => ({
        key: `nsd-ncd-${i}`,
        command: cmd,
        source: 'nsd-ncd',
        ncdIndex: i,
        sending: false,
        removable: true,
        color: undefined,
        tooltip: '',
      }))
      const orderedFromPriority = this.nsdPriority.flatMap(p =>
        p === 'next' ? nextItems : p === 'all' ? allItems : p === 'ncd' ? ncdItems : [],
      )
      // Si "ncd" absent de priority_listAction (config héritée), on l'ajoute en queue
      // pour que les commandes QuickControl restent visibles.
      const orderedFinal = this.nsdPriority.includes('ncd')
        ? orderedFromPriority
        : [...orderedFromPriority, ...ncdItems]
      return [...bufferItems, ...orderedFinal]
    },
    acceptedCommands() {
      const consoleMsgs = this.missionUiState?.consoleMsgs ?? []
      return consoleMsgs
        .filter(
          r =>
            r.gdbType === WobGbdType.GliderMessage &&
            typeof r.data === 'string' &&
            r.data.startsWith(ACCEPTED_CMD_PREFIX),
        )
        .map(r => ({
          command: r.data.slice(ACCEPTED_CMD_PREFIX.length),
          date: r.date ? new Date(r.date).toLocaleString() : '—',
          status: this.$t('Pilot.QuickControl.Tables.StatusAccepted'),
        }))
    },
    nsdNextList() {
      return this.originalNsdConfigs[this.gliId]?.mod_settings?.NextSurfacingDo_next?.listAction ?? []
    },
    nsdAllList() {
      return this.originalNsdConfigs[this.gliId]?.mod_settings?.NextSurfacingDo_all?.listAction ?? []
    },
    ncdList() {
      return this.originalNsdConfigs[this.gliId]?.mod_settings?.NextConnectDo?.listAction ?? []
    },
    nsdPriority() {
      return (
        this.originalNsdConfigs[this.gliId]?.mod_settings?.NextSurfacingDo?.priority_listAction ?? [
          'next',
          'all',
          'ncd',
        ]
      )
    },
    flasherPending() {
      return this.ncdList.some(cmd => cmd.startsWith('$flasher.enable='))
    },
    radioPending() {
      return this.ncdList.some(cmd => cmd.startsWith('$radio.enable='))
    },
    payloadPending() {
      return this.ncdList.some(cmd => cmd.startsWith('$pld1.enable='))
    },
    missionPlanActive() {
      return this.mission.gliderRuntime?.modules?.MissionPlan ?? false
    },
  },
  watch: {
    acceptedCommands() {
      this.$nextTick(() => {
        const el = this.$refs.acceptedScroll
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    refusedCommands() {
      this.$nextTick(() => {
        const el = this.$refs.refusedScroll
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    'mission.gliderWaitingMode'(newMode) {
      if (newMode === GLIDER_MODE.GLIDER_MODE_DISCONNECTED && this.commandBuffer.length > 0) {
        this.flushBufferToNSD()
      }
    },
  },
  created() {
    this.$store.myActions.UI_ENABLE_CONSOLE_MSG(this.misId)
  },
  mounted() {
    this.irisError = null
    if (this.gliId != null) {
      this.$store.myActions.GLIDER_IRIS_NSD_REQUEST(this.gliId)
    }
    this.$store.myActions.MISSION_IRIS_QUICKCONTROL_CONFIG_REQUEST(this.misId).catch(err => {
      this.irisError = this.getIrisErrorMessage(err)
    })
  },
  methods: {
    getIrisErrorMessage(err) {
      const codeMap = {
        ApirisTimeout: this.$t('Pilot.QuickControl.Errors.Timeout'),
        ApirisConnectionRefused: this.$t('Pilot.QuickControl.Errors.ConnectionRefused'),
        ApirisSocketError: this.$t('Pilot.QuickControl.Errors.SocketError'),
        ApirisNoBody: this.$t('Pilot.QuickControl.Errors.NoBody'),
      }
      return codeMap[err?.resultCode] || this.$t('Pilot.QuickControl.Errors.Generic')
    },

    buildCommands(type) {
      let cmds
      switch (type) {
        case 'dive': {
          const wm = this.mission.gliderWaitingMode
          const gliMode = this.mission.gliderRuntime?.mode
          const effectiveMode =
            wm === GLIDER_MODE.GLIDER_MODE_WAITING_FOR_ACK
              ? 2
              : wm === GLIDER_MODE.GLIDER_MODE_WAITING_FOR_RESUME
              ? 1
              : wm === GLIDER_MODE.GLIDER_MODE_DISCONNECTED
              ? gliMode ?? 0
              : 0
          cmds =
            effectiveMode === 2
              ? [`$clr=${this.activeAlarm}`, '$halt=0', '$go']
              : effectiveMode === 1
              ? ['$halt=0', '$go']
              : ['$go']
          break
        }
        case 'surface':
          cmds = [this.surfaceActive ? '$halt=0' : '$halt=1']
          break
        case 'ack_alarm':
          cmds = this.activeAlarm ? [`$clr=${this.activeAlarm}`] : []
          break
        case 'download':
          cmds = [`$sendFile=n-1,p-1`]
          break
        case 'flasher':
          cmds = [`$flasher.enable=${this.flasherEnabled ? 0 : 1}`]
          break
        case 'radio':
          cmds = [`$radio.enable=${this.radioEnabled ? 0 : 1}`]
          break
        case 'payload':
          cmds = [`$pld1.enable=${this.payloadEnabled ? 0 : 1}`]
          break
        default:
          cmds = []
      }
      return cmds.map(c => `${c};`)
    },

    enqueueCommands(commands) {
      commands.forEach(command => {
        this.commandBuffer.push({ command })
      })
      this.processBuffer()
    },

    waitForCommandAccepted(command) {
      const stripTrailingSemi = s => (typeof s === 'string' && s.endsWith(';') ? s.slice(0, -1) : s)
      const target = stripTrailingSemi(command)
      const matches = arr => arr.filter(c => stripTrailingSemi(c.command) === target).length
      return new Promise((resolve, reject) => {
        const countBefore = matches(this.acceptedCommands)
        const timer = setTimeout(() => {
          unwatch()
          reject(new Error('timeout'))
        }, 30000)
        const unwatch = this.$watch('acceptedCommands', newVal => {
          if (matches(newVal) > countBefore) {
            clearTimeout(timer)
            unwatch()
            resolve()
          }
        })
      })
    },

    async processBuffer() {
      if (this.bufferProcessing || !this.commandBuffer.length) return
      this.bufferProcessing = true
      while (this.commandBuffer.length > 0) {
        if (this.mission.gliderWaitingMode === GLIDER_MODE.GLIDER_MODE_DISCONNECTED) {
          await this.flushBufferToNSD()
          break
        }
        const { command } = this.commandBuffer[0]
        try {
          await this.$store.myActions.GLIDER_SEND_IRIS_COMMAND_REQUEST({ gliId: this.mission.gliId, command })
        } catch (err) {
          this.irisError = this.getIrisErrorMessage(err)
          this.refusedCommands.push({ command, date: new Date().toLocaleString() })
          this.commandBuffer.shift()
          continue
        }
        try {
          await this.waitForCommandAccepted(command)
          this.commandBuffer.shift()
          // L'UI suit désormais le runtime (alimenté par WobGliderNmea SEADEV
          // après que le glider a appliqué la commande) — plus de toggle local.
          await new Promise(r => setTimeout(r, 6000))
        } catch {
          this.refusedCommands.push({ command, date: new Date().toLocaleString() })
          this.commandBuffer.shift()
        }
      }
      this.bufferProcessing = false
    },

    async sendToNSD(commands) {
      if (!commands.length || this.mission.gliId == null) return
      await this.$store.myActions.GLIDER_IRIS_NSD_REQUEST(this.mission.gliId)
      const nsdConfig = this.$store.myGetters.originalNsdConfigs[this.mission.gliId]
      if (!nsdConfig) return
      const existingNcd = nsdConfig.mod_settings?.NextConnectDo?.listAction ?? []
      const wasModuleEnabled = nsdConfig.mod_settings?.EnabledMOD?.['module-NextSurfacingDo'] === true
      // priority_listAction : on préserve l'ordre utilisateur et on ajoute "ncd" en queue
      // s'il manque (sinon IRIS ne traite jamais le bloc NextConnectDo).
      const currentPriority = nsdConfig.mod_settings?.NextSurfacingDo?.priority_listAction ?? ['next', 'all']
      const newPriority = currentPriority.includes('ncd') ? currentPriority : [...currentPriority, 'ncd']
      const priorityChanged = newPriority.length !== currentPriority.length
      const nsdUpdate = priorityChanged ? { priority_listAction: newPriority } : undefined
      this.$store.myActions.GLIDER_UPDATE_MODIFIED_NSD({
        itemId: this.mission.gliId,
        newProperties: {
          mod_settings: {
            EnabledMOD: { 'module-NextSurfacingDo': true },
            ...(nsdUpdate ? { NextSurfacingDo: nsdUpdate } : {}),
            NextConnectDo: { listAction: [...commands, ...existingNcd] },
          },
        },
      })
      await this.$store.myActions.GLIDER_IRIS_NSD_SAVE_REQUEST(this.mission.gliId)
      if (!wasModuleEnabled) {
        this.$confirm(`<ul><li>${this.$t('Pilot.QuickControl.Nsd.ModuleEnabled')}</li></ul>`, {
          title: this.$t('Pilot.QuickControl.Nsd.AutoEnableTitle'),
          buttonTrueText: 'Ok',
          buttonFalseText: '',
        })
      }
    },

    async flushBufferToNSD() {
      const commands = this.commandBuffer.map(item => item.command)
      this.commandBuffer = []
      this.bufferProcessing = false
      await this.sendToNSD(commands)
    },

    async sendCommand(type) {
      const commands = this.buildCommands(type)
      if (!commands.length) return
      this.irisError = null
      if (this.mission.gliderWaitingMode === GLIDER_MODE.GLIDER_MODE_DISCONNECTED) {
        try {
          await this.sendToNSD(commands)
        } catch (err) {
          this.irisError = this.getIrisErrorMessage(err)
        }
      } else {
        this.enqueueCommands(commands)
      }
    },

    async activateMissionPlan() {
      this.missionPlanLoading = true
      try {
        await this.$store.myActions.MISSION_IRIS_MISSION_PLAN_CONFIG_ENABLE_REQUEST({
          itemId: this.misId,
          enable: true,
        })
      } catch (err) {
        this.irisError = this.getIrisErrorMessage(err)
      } finally {
        this.missionPlanLoading = false
      }
    },

    removePendingRow(row) {
      if (row.source === 'buffer') {
        this.commandBuffer.splice(row.bufferIndex, 1)
      } else if (row.source === 'nsd-ncd') {
        this.removeNcdCommand(row.ncdIndex)
      }
    },

    async removeNcdCommand(index) {
      const nsdConfig = this.$store.myGetters.originalNsdConfigs[this.gliId]
      if (!nsdConfig) return
      const newList = [...(nsdConfig.mod_settings?.NextConnectDo?.listAction ?? [])]
      newList.splice(index, 1)
      this.$store.myActions.GLIDER_UPDATE_MODIFIED_NSD({
        itemId: this.gliId,
        newProperties: {
          mod_settings: {
            NextConnectDo: { listAction: newList },
          },
        },
      })
      await this.$store.myActions.GLIDER_IRIS_NSD_SAVE_REQUEST(this.gliId)
    },

    onConfigSaved() {
      this.$store.myActions.MISSION_IRIS_QUICKCONTROL_CONFIG_REQUEST(this.misId)
    },
  },
}
</script>

<style scoped>
.cmd-side-by-side {
  display: flex;
  gap: 0;
  align-items: stretch;
}
.cmd-side {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.cmd-side + .cmd-side {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}
.cmd-side-title {
  padding: 6px 8px 4px;
  font-weight: 500;
  flex-shrink: 0;
}
.cmd-side-scroll {
  max-height: 150px;
  overflow-y: auto;
  flex: 1 1 auto;
}
.cmd-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.cmd-table th {
  text-align: center;
  padding: 2px 8px;
  color: rgba(0, 0, 0, 0.54);
  font-weight: 700;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  height: 28px;
}
.cmd-table td {
  padding: 2px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  height: 24px;
  vertical-align: middle;
}
</style>
